/* Nightpass - bouton « Voir plus » : ajoute la page suivante au lieu de la remplacer */

/*
 * Reutilise l'API de rendu de section de Shopify (`?section_id=`), la meme que
 * `facets.js` : on demande la page suivante, on ajoute ses cartes a la grille
 * en place, puis on recopie de la reponse l'URL de la page d'apres et le
 * compteur.
 *
 * C'est un element personnalise, et non un ecouteur pose au chargement : quand
 * un filtre change, `facets.js` reecrit tout #ProductGridContainer, bouton
 * compris. `connectedCallback` se rejoue alors tout seul sur le bloc reconstruit.
 */
class NpVoirPlus extends HTMLElement {
  connectedCallback() {
    // Le JS prend la main : la pagination numerotee passe en visually-hidden
    // (elle reste dans le DOM pour les robots) et le bouton s'affiche. Sans JS
    // rien de tout ca n'arrive, et la page se navigue a la pagination.
    this.classList.add('np-voir-plus--js');
    this.bouton = this.querySelector('.np-voir-plus__bouton');
    if (!this.bouton) return;
    this.bouton.hidden = false;
    this.bouton.addEventListener('click', () => this.charger());
  }

  async charger() {
    if (this.occupe || !this.dataset.suivant) return;
    this.occupe = true;
    this.classList.add('np-voir-plus--occupe');
    this.bouton.setAttribute('aria-busy', 'true');

    const url = new URL(this.dataset.suivant, window.location.origin);
    url.searchParams.set('section_id', this.dataset.section);

    try {
      const reponse = await fetch(url.toString());
      if (!reponse.ok) throw new Error('HTTP ' + reponse.status);
      this.integrer(new DOMParser().parseFromString(await reponse.text(), 'text/html'));
    } catch (erreur) {
      // Rien ne casse en silence : on rend la main a la pagination numerotee,
      // qui est deja dans le DOM. Il suffit d'arreter de la masquer.
      console.error('np-voir-plus :', erreur);
      this.classList.remove('np-voir-plus--js');
      this.bouton.hidden = true;
    } finally {
      this.occupe = false;
      this.classList.remove('np-voir-plus--occupe');
      this.bouton.removeAttribute('aria-busy');
    }
  }

  integrer(doc) {
    const source = NpVoirPlus.grille(doc);
    const cible = NpVoirPlus.grille(document);
    if (!source || !cible) throw new Error('grille introuvable');

    // insertAdjacentHTML plutot qu'un append de noeuds venus du DOMParser :
    // le fragment est analyse dans le document vivant, donc les elements
    // personnalises des cartes (quick-add, product-form) sont bien construits.
    const avant = cible.children.length;
    cible.insertAdjacentHTML('beforeend', source.innerHTML);
    const ajoutees = Array.from(cible.children).slice(avant);

    // Les cartes de Dawn demarrent a opacity 0 et sont revelees par un
    // IntersectionObserver installe au chargement, qui n'observe pas ce qui
    // arrive apres. Sans cette classe, les nouvelles resteraient invisibles.
    ajoutees.forEach((carte) => {
      carte.classList.add('scroll-trigger--cancel');
      carte.querySelectorAll('.scroll-trigger').forEach((e) => e.classList.add('scroll-trigger--cancel'));
    });

    // L'URL suit la derniere page chargee : revenir depuis une fiche produit
    // ramene au bon endroit du catalogue. `replaceState` et non `pushState` --
    // empiler une entree par chargement obligerait a dix retours pour quitter
    // la page.
    const affichee = new URL(this.dataset.suivant, window.location.origin);
    history.replaceState({}, '', affichee.pathname + affichee.search);

    // On met ce bloc a jour au lieu de le remplacer : le focus reste sur le
    // bouton, et `.np-voir-plus__compte` est une region `role="status"` dont le
    // texte change -- deux choses qu'un remplacement casserait.
    const neuf = doc.querySelector('np-voir-plus');
    this.dataset.suivant = (neuf && neuf.dataset.suivant) || '';
    NpVoirPlus.recopier(this, neuf, '.np-voir-plus__compte', 'textContent');
    NpVoirPlus.recopier(this, neuf, '.np-voir-plus__repli', 'innerHTML');

    if (!this.dataset.suivant) {
      // Derniere page : le bouton disparait, le focus clavier doit atterrir
      // quelque part plutot que de retomber sur <body>.
      this.bouton.hidden = true;
      if (ajoutees[0]) {
        ajoutees[0].setAttribute('tabindex', '-1');
        ajoutees[0].focus({ preventScroll: true });
      }
    }
  }

  static recopier(ici, la, selecteur, propriete) {
    const a = ici.querySelector(selecteur);
    const b = la && la.querySelector(selecteur);
    if (a && b) a[propriete] = b[propriete];
  }

  // Page collection : #product-grid EST la liste. Page recherche : c'est le
  // conteneur qui l'enveloppe. Les deux sections sont servies par ce bouton.
  static grille(racine) {
    const bloc = racine.getElementById('product-grid');
    if (!bloc) return null;
    return bloc.matches('ul') ? bloc : bloc.querySelector('ul.product-grid');
  }
}

customElements.define('np-voir-plus', NpVoirPlus);
